// src/redux/slices/userSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";

// 📌 Fetch all users
export const fetchUsers = createAsyncThunk(
  "users/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get("/user/all-users");
      console.log("users data", data)
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Something went wrong" });
    }
  }
);

// 📌 Fetch all admins
export const fetchAdmins = createAsyncThunk(
  "users/fetchAdmins",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get("/admins");

      // ✅ normalize response
      if (Array.isArray(data)) return data;
      if (data.admins) return data.admins;
      if (data.data) return data.data;

      return [];
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Something went wrong" });
    }
  }
);




// 📌 Get single user by ID
export const fetchSingleUser = createAsyncThunk(
  "users/fetchSingle",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/single-user/${id}`);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Something went wrong" });
    }
  }
);

// 📌 Update user
export const updateUser = createAsyncThunk(
  "users/updateUser",
  async ({ id, userData }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`/user/update-user/${id}`, userData);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Something went wrong" });
    }
  }
);

// 📌 Update admin
export const updateAdmin = createAsyncThunk(
  "users/updateAdmin",
  async ({ id, userData }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`/admin/update/${id}`, userData);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Something went wrong" });
    }
  }
);


// 📌 Delete user
export const deleteUser = createAsyncThunk(
  "users/deleteUser",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.delete(`/user/delete-user/${id}`);
      return { ...data, id };
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Something went wrong" });
    }
  }
);


// 📌 Delete admin
export const deleteAdmin = createAsyncThunk(
  "users/deleteAdmin",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.delete(`/admin/delete/${id}`);
      return { ...data, id };
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Something went wrong" });
    }
  }
);


/* ===============================
   THUNK : FETCH DASHBOARD STATS
================================ */
export const fetchVendorDashboardStats = createAsyncThunk(
  "vendorDashboard/fetchStats",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/vendor/dashboard-counts",);

      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || err.message
      );
    }
  }
);


const initialState = {
  users: [],
  admins: [],
  selectedUser: null,
  stats: {
    pendingBookings: 0,
    confirmedBookings: 0,
    newAppointments: 0,
    totalBills: 0,
    totalReviews: 0,
  },
  loading: false,
  error: null,
  success: null,
};

const userSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    clearUserMessages: (state) => {
      state.error = null;
      state.success = null;
    },
    clearVendorDashboardError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Users
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          state.users = action.payload.data;
        } else {
          state.error = action.payload.message;
        }
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message;
      })

      // Fetch Admins
      .addCase(fetchAdmins.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAdmins.fulfilled, (state, action) => {
        state.loading = false;
        state.admins = action.payload;
      })
      .addCase(fetchAdmins.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message;
      })

      // Fetch Single User
      .addCase(fetchSingleUser.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.selectedUser = action.payload.data;
        } else {
          state.error = action.payload.message;
        }
      })

      // Update User
      .addCase(updateUser.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.users = state.users.map((u) =>
            u._id === action.payload.data._id ? action.payload.data : u
          );
          state.success = action.payload.message;
        } else {
          state.error = action.payload.message;
        }
      })

      // Update Admin
      .addCase(updateAdmin.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.admins = state.admins.map((a) =>
            a._id === action.payload.data._id ? action.payload.data : a
          );
          state.success = action.payload.message;
        } else {
          state.error = action.payload.message;
        }
      })

      // Delete User
      .addCase(deleteUser.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.users = state.users.filter((u) => u._id !== action.payload.id);
          state.success = action.payload.message;
        } else {
          state.error = action.payload.message;
        }
      })

      // Delete Admin
      .addCase(deleteAdmin.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.admins = state.admins.filter((a) => a._id !== action.payload.id);
          state.success = action.payload.message;
        } else {
          state.error = action.payload.message;
        }
      })

      /* ===== FETCH DASHBOARD STATS ===== */
      .addCase(fetchVendorDashboardStats.pending, (state) => {
        state.loading = true;
      })

      .addCase(fetchVendorDashboardStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload;
      })

      .addCase(fetchVendorDashboardStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

  },
});

export const { clearUserMessages } = userSlice.actions;
export default userSlice.reducer;
